import { createSelector } from 'reselect';
import { flatten } from 'ramda';
import { MOET_CONSTANTS } from 'moet-core';

import {
  getMerchant,
  getMerchantCountryCurrency,
  getMerchantCountryKey,
  getUserMerchantId,
  hasMerchantSubmittedBusinessDetails,
} from './common';
import {
  getCompatibleAndSortedProducts,
  getSortedProducts,
  getSelectedProductId,
  getSelectedProductOptionIds,
  getSelectedProduct,
  getProductData,
  isKlarnaOneAvailable,
} from '../products';
import { countrySpecificTranslationParser } from '../utils';
import { isUnmanagedKyc } from '../widget';
import { getTermsAndConditionsForCountry } from '@/utils/termsAndConditions';

const { PRODUCT_STATUS } = MOET_CONSTANTS;

export const getMerchantProducts = createSelector(
  getMerchant,
  getCompatibleAndSortedProducts,
  ({ products: merchantProducts = [] }, allProducts = []) =>
    merchantProducts.map(product => {
      const matchedProduct = allProducts.find(p => p.id === product.id) || { options: [] };
      return {
        ...matchedProduct,
        ...product,
        options:
          product.options && matchedProduct.options
            ? product.options.map(option => {
                const matchedOption = matchedProduct.options.find(o => o.id === option.id);
                return {
                  ...matchedOption,
                  ...option,
                };
              })
            : [],
      };
    }),
);

export const getMerchantProductNames = createSelector(
  getMerchantProducts,
  getMerchantCountryKey,
  countrySpecificTranslationParser,
  (products = [], country, t) => {
    const names = [];
    const tPrefix = 'moet-core.products';
    products.forEach(p => {
      if (p.options && p.options.every(({ required = true }) => required)) {
        names.push(t(`${tPrefix}.${p.name}`, country));
      } else {
        names.push(
          ...(p.options || [])
            .filter(o => o.required !== true)
            .map(o => t(`${tPrefix}.${o.name}`, country)),
        );
      }
    });
    return names;
  },
);

export const getExtraContextProps = createSelector(getMerchantProductNames, productNames => ({
  productNames,
}));

export const getDataForAddingProducts = createSelector(
  getSelectedProductId,
  getSelectedProductOptionIds,
  getUserMerchantId,
  (productId, options, mid) => {
    return {
      body: [
        {
          id: productId,
          options:
            options.length > 0
              ? options
                  .filter(p => p.checked && (!p.status || p.status === PRODUCT_STATUS.IN_PROGRESS))
                  .map(opt => ({ id: opt.id, resourceId: opt.resourceId }))
              : undefined,
        },
      ],
      params: { mid },
    };
  },
);

const _getProductStatus = (products, id, hasSubmittedBusinessDetails) => {
  if (products && products.length > 0 && hasSubmittedBusinessDetails) {
    const option = flatten(
      products.map(p => p.options.filter(pr => pr.id === id).map(pro => pro.status)),
    );
    const product = products.find(p => p.id === id);
    if (option.length > 0) {
      return option[0];
    } else if (product) {
      return product.status;
    }
  }
  return undefined;
};

export const getMerchantProductsWithStatus = createSelector(
  getCompatibleAndSortedProducts,
  getSortedProducts,
  hasMerchantSubmittedBusinessDetails,
  getMerchantProducts,
  isKlarnaOneAvailable,
  (
    compatibleProductsWithOptions,
    productsWithOptions,
    hasSubmittedBusinessDetails,
    products,
    isKlarnaOneAvailable,
  ) => {
    const allProducts = isKlarnaOneAvailable ? productsWithOptions : compatibleProductsWithOptions;
    const productList = allProducts
      .map(product => {
        return {
          ...product,
          status: _getProductStatus(products, product.id, hasSubmittedBusinessDetails),
          options: product.options
            .filter(p => p.id !== 'kco-address-widget' && (isKlarnaOneAvailable || p.isCompatible))
            .map(option => {
              return {
                ...option,
                status: _getProductStatus(products, option.id, hasSubmittedBusinessDetails),
              };
            }),
        };
      })
      .filter(prod => prod.options.length > 0 && (isKlarnaOneAvailable || prod.isCompatible));
    return productList;
  },
);

export const getInstalledOrInProgressProductId = createSelector(getMerchantProducts, products => {
  const product = products.find(
    ({ status, options }) => status || options.find(({ status }) => status),
  );
  const hasProductOngoing =
    product && product.status && product.status !== PRODUCT_STATUS.NOT_STARTED;
  const hasOptionsOngoing =
    product &&
    product.options &&
    product.options.filter(p => p.status !== PRODUCT_STATUS.NOT_STARTED).length > 0;
  return hasProductOngoing || hasOptionsOngoing ? product.id : '';
});

export const getSelectedProductTermAndConditionsLink = createSelector(
  getMerchant,
  getSelectedProduct,
  (merchant, product) => {
    if (product && merchant) {
      return getTermsAndConditionsForCountry(product.termsAndConditions, merchant.country).href;
    }
    return null;
  },
);

export const getDataForAdditionalProducts = createSelector(
  getDataForAddingProducts,
  getSelectedProductTermAndConditionsLink,
  (dataForAddingProducts, tocLink) => {
    const data = {
      ...dataForAddingProducts,
      body: dataForAddingProducts.body.map(body => {
        return {
          ...body,
          tocLink: tocLink,
        };
      }),
    };
    return data;
  },
);

export const hasSelectedProduct = createSelector(
  getSelectedProduct,
  isUnmanagedKyc,
  (product, isUnmanagedKyc) => !isUnmanagedKyc || (product !== null && product),
);

export const hasPersistedProduct = createSelector(
  getMerchantProducts,
  isUnmanagedKyc,
  (products, isUnmanagedKyc) => !isUnmanagedKyc || (products && products.length > 0),
);

/** ************ ADDITIONAL PRODUCTS PAGE SELECTORS **************/

export const getSelectedProductPrice = createSelector(
  getMerchantCountryKey,
  getSelectedProductId,
  (countryKey, products) =>
    countryKey && products && products.prices
      ? products.prices.find(p => p.country === countryKey)
      : null,
);

export const getSelectedAndNotAcceptedOptionIds = createSelector(
  getSelectedProductOptionIds,
  selectedProductOptionIds =>
    selectedProductOptionIds
      .filter(({ status, checked }) => checked && status === PRODUCT_STATUS.IN_PROGRESS)
      .map(p => p.id),
);

export const getProductOptionsForAdditionalProducts = createSelector(
  getProductData,
  getSelectedAndNotAcceptedOptionIds,
  getSelectedProductId,
  (data, selectedOptionIds, selectedProductId) => {
    const selectedProduct = data.find(product => product.id === selectedProductId);
    if (typeof selectedProduct !== 'undefined') {
      if (selectedOptionIds.length > 0) {
        return selectedProduct.options.filter(opt => selectedOptionIds.includes(opt.id));
      } else {
        return [
          {
            id: selectedProduct.id,
            prices: selectedProduct.prices,
            checked: true,
          },
        ];
      }
    }
    return [];
  },
);

export const getProductOptionsWithPricesAndNames = createSelector(
  getProductOptionsForAdditionalProducts,
  getMerchantCountryKey,
  countrySpecificTranslationParser,
  getSelectedProductId,
  (selectedOptions, merchantCountry, t, selectedProductId) => {
    const tPrefix = 'moet-core.products';
    return selectedOptions.map(opt => {
      return {
        ...opt,
        price: opt.prices
          ? opt.prices.filter(price => price.consumerCountry === merchantCountry)[0]
          : null,
        name:
          t(`${tPrefix}.${opt.id}.name`, merchantCountry) ||
          t(`${tPrefix}.${selectedProductId}.options.${opt.id}.name`, merchantCountry),
      };
    });
  },
);

export const hasProductsWithNoPrice = createSelector(
  getProductOptionsWithPricesAndNames,
  products => products.find(p => !p.price) !== undefined,
);

/** ************ GLOBAL PRICES DIALOG SELECTOR **************/

export const getSelectedProductGlobalPrices = createSelector(
  getSelectedProduct,
  getMerchantCountryCurrency,
  (product = {}, currencyCodeOfMerchant) => {
    if (!product.prices) {
      return [];
    }
    if (!currencyCodeOfMerchant) {
      currencyCodeOfMerchant = 'EUR';
    }

    const localPrices = product.prices.filter(p => p.country);
    const globalPrices = product.prices.filter(p => p.country === null);
    const globalPriceToShow =
      globalPrices.find(price => price.currency_code === currencyCodeOfMerchant) ||
      globalPrices.find(price => price.currency_code === 'EUR');
    if (globalPriceToShow) {
      return [...localPrices, globalPriceToShow];
    }

    return localPrices;
  },
);

export const hasNonKlarnaOneProducts = createSelector(
  getMerchantProducts,
  products => !products.every(p => p.id.includes('one')),
);
